'use client';

import type { RangeFilterValue } from '@vs-shop/schemas/product/filter/state/range';
import { clampRange } from '@vs-shop/schemas/product/filter/state/rangeValidation';
import {
  FilterRangeBounds,
  FilterRangeFacet,
  FilterRangeSeparator,
} from '@vs-shop/commerce-ui/filterRange';
import { BigDecimal, Option } from 'effect';
import { useMemo } from 'react';
import type { ReactNode } from 'react';

import { createRangeFilterStrategy } from './RangeFilterStrategy';
import FilterRangeInput from '../../server/filter/FilterRangeInput';
import FilterRangeSlider from '../../server/filter/FilterRangeSlider';

type Props = {
  readonly rangeValue: RangeFilterValue;
  readonly selectedMin: BigDecimal.BigDecimal;
  readonly selectedMax: BigDecimal.BigDecimal;
  readonly unit: ReactNode;
  readonly step: BigDecimal.BigDecimal;
  readonly onValueChange: (value: RangeFilterValue) => void;
};

/** Uses the same range strategies for legacy and shared listing state. */
export const FilterRangeControl = ({
  rangeValue,
  selectedMin,
  selectedMax,
  unit,
  step,
  onValueChange,
}: Props) => {
  // Create strategy for value transformations (linear for non-price attributes)
  const strategy = useMemo(() => createRangeFilterStrategy(rangeValue, step), [rangeValue, step]);

  const sliderConfig = strategy.getSliderConfig();

  // Convert current values to slider positions using strategy
  const sliderMin = strategy.valueToSlider(selectedMin);
  const sliderMax = strategy.valueToSlider(selectedMax);

  const handleMinChange = (val: BigDecimal.BigDecimal) => {
    // Clamp the input against the current opposite bound.
    const clamped = clampRange(val, selectedMax, rangeValue.min, rangeValue.max);

    onValueChange(clamped);
  };

  const handleMaxChange = (val: BigDecimal.BigDecimal) => {
    // Clamp the input against the current opposite bound.
    const clamped = clampRange(selectedMin, val, rangeValue.min, rangeValue.max);

    onValueChange(clamped);
  };

  const handleSliderChange = ([cMin, cMax]: readonly [number, number]) => {
    // Convert slider positions to actual values using strategy
    const actualMin = strategy.sliderToValue(cMin);
    const actualMax = strategy.sliderToValue(cMax);

    // Round the bounds to the configured precision.
    const dividedMinOption = BigDecimal.divide(actualMin, step);
    const dividedMaxOption = BigDecimal.divide(actualMax, step);

    if (Option.isNone(dividedMinOption) || Option.isNone(dividedMaxOption)) {
      return; // Skip update to avoid invalid state
    }

    const roundedMin = BigDecimal.floor(dividedMinOption.value);
    const roundedMax = BigDecimal.ceil(dividedMaxOption.value);

    const steppedMin = BigDecimal.multiply(roundedMin, step);
    const steppedMax = BigDecimal.multiply(roundedMax, step);

    // Clamp to valid range
    const clamped = clampRange(steppedMin, steppedMax, rangeValue.min, rangeValue.max);

    onValueChange(clamped);
  };

  return (
    <FilterRangeFacet>
      {/* The gap to the track is space between the two, so the composition
          sets it rather than either part carrying it. */}
      <FilterRangeBounds className="mb-4">
        <FilterRangeInput
          value={selectedMin}
          min={rangeValue.min}
          max={selectedMax}
          step={step}
          onChange={handleMinChange}
        >
          {unit}
        </FilterRangeInput>
        <FilterRangeSeparator />
        <FilterRangeInput
          value={selectedMax}
          min={selectedMin}
          max={rangeValue.max}
          step={step}
          onChange={handleMaxChange}
        >
          {unit}
        </FilterRangeInput>
      </FilterRangeBounds>
      <FilterRangeSlider
        min={sliderConfig.min}
        max={sliderConfig.max}
        step={sliderConfig.step}
        minValue={sliderMin}
        maxValue={sliderMax}
        onValueChange={handleSliderChange}
      />
    </FilterRangeFacet>
  );
};
